import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { pilotDocumentFetchUrl } from '../utils/pilotDocumentUrl';

export default function PilotProfileChangesQueue({ onSuccess, onError, onCountChange }) {
  const { t, i18n } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const loc = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/pilot-profile/change-requests', { params: { status: 'pending' } });
      const list = res.data.requests || [];
      setRequests(list);
      onCountChange?.(list.length);
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.dataLoadError'));
    } finally {
      setLoading(false);
    }
  }, [onCountChange, onError, t]);

  useEffect(() => {
    load();
  }, [load]);

  const serveDoc = async (docPath) => {
    if (!docPath) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(pilotDocumentFetchUrl(docPath), {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('load failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch {
      onError?.(t('admin.messages.docLoadErr'));
    }
  };

  const approve = async (id) => {
    try {
      await axios.put(`/api/pilot-profile/change-requests/${id}/approve`);
      onSuccess?.(t('admin.pilotProfile.changeApproved'));
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.pilotApplications.genericError'));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt(t('admin.pilotProfile.rejectPrompt'));
    if (reason === null) return;
    try {
      await axios.put(`/api/pilot-profile/change-requests/${id}/reject`, { reason: reason || '' });
      onSuccess?.(t('admin.pilotProfile.changeRejected'));
      await load();
    } catch (err) {
      onError?.(err.response?.data?.error || t('admin.pilotApplications.genericError'));
    }
  };

  if (loading) {
    return <p className="loading">{t('admin.loading')}</p>;
  }

  return (
    <div className="pilot-profile-changes-queue">
      <h3 className="dashboard-subsection-title">{t('admin.pilotProfile.changesQueueTitle')}</h3>
      <p className="section-desc">{t('admin.pilotProfile.changesQueueIntro')}</p>
      {requests.length === 0 ? (
        <div className="card">
          <p>{t('admin.pilotProfile.changesQueueEmpty')}</p>
        </div>
      ) : (
        <div className="card">
          <div className="pilot-applications-list">
            {requests.map((r) => (
              <div key={r.id} className="pilot-application-item">
                <div className="pilot-application-info">
                  <div className="pilot-application-name-row">
                    <strong>{r.pilotName}</strong>
                    <span className="pilot-app-status pilot-app-status-pending">
                      {r.changeKind === 'document'
                        ? t('admin.pilotProfile.pendingDoc')
                        : t('admin.pilotProfile.pendingProfile')}
                    </span>
                  </div>
                  <p>{r.pilotEmail}</p>
                  <p>
                    {t('admin.pilotProfile.submittedAt')}{' '}
                    {r.createdAt ? new Date(r.createdAt).toLocaleString(loc) : '—'}
                  </p>
                  <pre className="pilot-change-summary">{r.summary}</pre>
                  {r.changeKind === 'document' && r.payload?.path && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => serveDoc(r.payload.path)}
                    >
                      {t('admin.pilotProfile.viewDocument')}
                    </button>
                  )}
                </div>
                <div className="instructor-actions">
                  <button type="button" className="btn btn-success btn-sm" onClick={() => approve(r.id)}>
                    {t('admin.pilotProfile.approveChange')}
                  </button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => reject(r.id)}>
                    {t('admin.pilotProfile.rejectChange')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
